import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from '@/lib/api'
import { idempotencyKey } from '@/lib/utils'
import type { components } from '@/api/schema'

/**
 * Org area: organisation administration. Every key starts with ['org', orgId]
 * so a mutation can refresh everything the organisation shows at once.
 */

type S = components['schemas']
export type OrgSettings = S['OrgSettings']
export type OrgMember = S['OrgMember']
export type OrgMemberPage = S['OrgMemberPage']
export type OrgInvitation = S['OrgInvitation']
export type InviteResult = S['InviteResult']
export type OrgJoinRequest = S['OrgJoinRequest']
export type OrgTeam = S['OrgTeam']
export type OrgDomain = S['OrgDomain']
export type OrgRole = S['OrgRole']
export type OrgRoleCatalog = S['OrgRoleCatalog']
export type OrgWorkspace = S['OrgWorkspace']
export type OrgWorkspaceList = S['OrgWorkspaceList']
export type SupportGrant = NonNullable<S['SupportGrant']>
export type SupportAccess = S['SupportAccess']

const idem = () => ({ 'Idempotency-Key': idempotencyKey() })

function useOrgMutation<I, O>(orgId: string, fn: (input: I) => Promise<O>, alsoSession = false) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['org', orgId] })
      if (alsoSession) void qc.invalidateQueries({ queryKey: ['session'] })
    },
  })
}

/* ─────────────────────────── settings ─────────────────────────── */

export const orgSettingsQuery = (orgId: string) =>
  queryOptions({ queryKey: ['org', orgId, 'settings'] as const, queryFn: () => unwrap(api.GET('/esm/v1/org/settings')) })

export const useRenameOrg = (orgId: string) =>
  useOrgMutation(orgId, (body: { name: string }) => unwrap(api.PATCH('/esm/v1/org/settings', { body })), true)

export const useSaveSecurity = (orgId: string) =>
  useOrgMutation(orgId, (body: { require_mfa: boolean; session_hours: number | null; allowed_domains: string[] }) => unwrap(api.PUT('/esm/v1/org/security', { body })), true)

export const useUploadLogo = (orgId: string) =>
  useOrgMutation(orgId, (body: { content_type: string; data_base64: string }) => unwrap(api.POST('/esm/v1/org/logo', { body })))

export const useRemoveLogo = (orgId: string) => useOrgMutation(orgId, () => unwrap(api.DELETE('/esm/v1/org/logo')))

export const useTransferOwnership = (orgId: string) =>
  useOrgMutation(orgId, (body: { user_id: string }) => unwrap(api.POST('/esm/v1/org/owner', { body, headers: idem() })))

export const useCloseOrg = (orgId: string) =>
  useOrgMutation(orgId, (body: { confirm_handle: string }) => unwrap(api.POST('/esm/v1/org/close', { body })), true)

/* ─────────────────────────── domains ─────────────────────────── */

export const domainsQuery = (orgId: string) =>
  queryOptions({ queryKey: ['org', orgId, 'domains'] as const, queryFn: () => unwrap(api.GET('/esm/v1/org/domains')) })

export const useAddDomain = (orgId: string) =>
  useOrgMutation(orgId, (body: { domain: string }) => unwrap(api.POST('/esm/v1/org/domains', { body, headers: idem() })))

export const useVerifyDomain = (orgId: string) =>
  useOrgMutation(orgId, (id: string) => unwrap(api.POST('/esm/v1/org/domains/{domain_id}/verify', { params: { path: { domain_id: id } } })))

export const useDomainPolicy = (orgId: string) =>
  useOrgMutation(orgId, ({ id, ...body }: { id: string; auto_join: 'off' | 'join' | 'request'; auto_join_role: string }) =>
    unwrap(api.PATCH('/esm/v1/org/domains/{domain_id}', { params: { path: { domain_id: id } }, body })))

export const useRemoveDomain = (orgId: string) =>
  useOrgMutation(orgId, (id: string) => unwrap(api.DELETE('/esm/v1/org/domains/{domain_id}', { params: { path: { domain_id: id } } })))

/* ─────────────────────────── members ─────────────────────────── */

export interface MemberFilters {
  q?: string
  role_id?: string
  status?: 'all' | 'active' | 'disabled'
}

export const membersQuery = (orgId: string, f: MemberFilters = {}) =>
  queryOptions({
    queryKey: ['org', orgId, 'members', f.q ?? '', f.role_id ?? '', f.status ?? 'all'] as const,
    queryFn: () => unwrap(api.GET('/esm/v1/org/members', { params: { query: { q: f.q || undefined, role_id: f.role_id || undefined, status: f.status ?? 'all', limit: 200 } } })),
  })

export const useChangeRole = (orgId: string) =>
  useOrgMutation(orgId, ({ id, role_id }: { id: string; role_id: string }) =>
    unwrap(api.PATCH('/esm/v1/org/members/{member_id}', { params: { path: { member_id: id } }, body: { role_id } })), true)

export const useDeactivate = (orgId: string) =>
  useOrgMutation(orgId, (id: string) => unwrap(api.POST('/esm/v1/org/members/{member_id}/deactivate', { params: { path: { member_id: id } } })))

export const useReactivate = (orgId: string) =>
  useOrgMutation(orgId, (id: string) => unwrap(api.POST('/esm/v1/org/members/{member_id}/reactivate', { params: { path: { member_id: id } } })))

export const useRemoveMember = (orgId: string) =>
  useOrgMutation(orgId, ({ id, reassign_to }: { id: string; reassign_to?: string }) =>
    unwrap(api.POST('/esm/v1/org/members/{member_id}/remove', { params: { path: { member_id: id } }, body: reassign_to ? { reassign_to } : {} })))

/* ─────────────────────────── invitations ─────────────────────────── */

export const invitationsQuery = (orgId: string, status: 'pending' | 'all' = 'pending') =>
  queryOptions({ queryKey: ['org', orgId, 'invitations', status] as const, queryFn: () => unwrap(api.GET('/esm/v1/org/invitations', { params: { query: { status } } })) })

export const useInvite = (orgId: string) =>
  useOrgMutation(orgId, ({ key, ...body }: { emails: string[]; role_id: string; workspace_ids: string[]; key: string }) =>
    unwrap(api.POST('/esm/v1/org/invitations', { body, headers: { 'Idempotency-Key': key } })))

export const useResendInvite = (orgId: string) =>
  useOrgMutation(orgId, (id: string) => unwrap(api.POST('/esm/v1/org/invitations/{invitation_id}/resend', { params: { path: { invitation_id: id } } })))

export const useRevokeInvite = (orgId: string) =>
  useOrgMutation(orgId, (id: string) => unwrap(api.POST('/esm/v1/org/invitations/{invitation_id}/revoke', { params: { path: { invitation_id: id } } })))

/* ─────────────────────────── join requests ─────────────────────────── */

export const joinRequestsQuery = (orgId: string) =>
  queryOptions({ queryKey: ['org', orgId, 'join-requests'] as const, queryFn: () => unwrap(api.GET('/esm/v1/org/join-requests')) })

export const useApproveJoin = (orgId: string) =>
  useOrgMutation(orgId, ({ id, role_id }: { id: string; role_id: string }) =>
    unwrap(api.POST('/esm/v1/org/join-requests/{request_id}/approve', { params: { path: { request_id: id } }, body: { role_id } })))

export const useDenyJoin = (orgId: string) =>
  useOrgMutation(orgId, (id: string) => unwrap(api.POST('/esm/v1/org/join-requests/{request_id}/deny', { params: { path: { request_id: id } } })))

/* ─────────────────────────── teams ─────────────────────────── */

export const teamsQuery = (orgId: string) =>
  queryOptions({ queryKey: ['org', orgId, 'teams'] as const, queryFn: () => unwrap(api.GET('/esm/v1/org/teams')) })

export interface TeamInput {
  name: string
  description: string
  member_ids: string[]
  workspace_ids: string[]
}

export const useCreateTeam = (orgId: string) =>
  useOrgMutation(orgId, (body: TeamInput) => unwrap(api.POST('/esm/v1/org/teams', { body, headers: idem() })), true)

export const useUpdateTeam = (orgId: string) =>
  useOrgMutation(orgId, ({ id, ...body }: Partial<TeamInput> & { id: string }) =>
    unwrap(api.PATCH('/esm/v1/org/teams/{team_id}', { params: { path: { team_id: id } }, body })), true)

export const useDeleteTeam = (orgId: string) =>
  useOrgMutation(orgId, (id: string) => unwrap(api.DELETE('/esm/v1/org/teams/{team_id}', { params: { path: { team_id: id } } })), true)

/* ─────────────────────────── roles ─────────────────────────── */

export const rolesQuery = (orgId: string) =>
  queryOptions({ queryKey: ['org', orgId, 'roles'] as const, queryFn: () => unwrap(api.GET('/esm/v1/org/roles')) })

export interface RoleInput {
  name: string
  description: string
  permissions: string[]
  priority: number
}

export const useCreateRole = (orgId: string) =>
  useOrgMutation(orgId, ({ key, ...body }: RoleInput & { key: string }) =>
    unwrap(api.POST('/esm/v1/org/roles', { body: body as never, headers: { 'Idempotency-Key': key } })))

export const useUpdateRole = (orgId: string) =>
  useOrgMutation(orgId, ({ role_id, ...body }: Partial<RoleInput> & { role_id: string }) =>
    unwrap(api.PATCH('/esm/v1/org/roles/{role_id}', { params: { path: { role_id } }, body: body as never })), true)

export const useDuplicateRole = (orgId: string) =>
  useOrgMutation(orgId, ({ role_id, name, key }: { role_id: string; name: string; key: string }) =>
    unwrap(api.POST('/esm/v1/org/roles/{role_id}/duplicate', { params: { path: { role_id } }, body: { name }, headers: { 'Idempotency-Key': key } })))

export const useDeleteRole = (orgId: string) =>
  useOrgMutation(orgId, (role_id: string) => unwrap(api.DELETE('/esm/v1/org/roles/{role_id}', { params: { path: { role_id } } })))

/* ─────────────────────────── workspaces ─────────────────────────── */

export const orgWorkspacesQuery = (orgId: string, status: 'all' | 'active' | 'archived' = 'all') =>
  queryOptions({ queryKey: ['org', orgId, 'workspaces', status] as const, queryFn: () => unwrap(api.GET('/esm/v1/org/workspaces', { params: { query: { status } } })) })

export interface WorkspaceInput {
  name: string
  description: string
  visibility: 'private' | 'shared'
  member_ids: string[]
  team_ids: string[]
}

export const useCreateWorkspace = (orgId: string) =>
  useOrgMutation(orgId, ({ key, ...body }: WorkspaceInput & { key: string }) => unwrap(api.POST('/esm/v1/org/workspaces', { body, headers: { 'Idempotency-Key': key } })), true)

export const useTransferWorkspace = (orgId: string) =>
  useOrgMutation(orgId, ({ id, user_id }: { id: string; user_id: string }) =>
    unwrap(api.POST('/esm/v1/org/workspaces/{workspace_id}/owner', { params: { path: { workspace_id: id } }, body: { user_id } })), true)

/* ─────────────────────────── support access ─────────────────────────── */

export const supportQuery = (orgId: string) =>
  queryOptions({ queryKey: ['org', orgId, 'support'] as const, queryFn: () => unwrap(api.GET('/esm/v1/org/support-access')), refetchInterval: 60_000 })

export const useGrantSupport = (orgId: string) =>
  useOrgMutation(orgId, ({ key, ...body }: { reason: string; duration: '1h' | '24h' | '7d'; level: 'metadata' | 'content'; key: string }) =>
    unwrap(api.POST('/esm/v1/org/support-access', { body, headers: { 'Idempotency-Key': key } })))

export const useRevokeSupport = (orgId: string) =>
  useOrgMutation(orgId, (id: string) => unwrap(api.POST('/esm/v1/org/support-access/{grant_id}/revoke', { params: { path: { grant_id: id } } })))
